/**
 * Itens que pedem ação — "Precisa da sua atenção" (GUPM-206/221/303) e sino do header (C-24).
 * TS puro.
 *
 * Vivia no `useM1Data`. Saiu para o domínio no C-24 porque o sino do header precisa da
 * MESMA lista que o Painel: mesmo nível, mesma regra de sem sinal (`semSinalVisivel`) e
 * mesma supressão por TARP em curso. Duas cópias desta derivação divergiriam no primeiro
 * ajuste — e o sino diria uma coisa enquanto o Painel diz outra.
 */
import { foraDeServico } from '../instrumentos/fora-de-servico';
import { unitForType } from '../instrumentos/instrument-type';
import { leituraGovernante, semSinalVisivel } from '../instrumentos/nivel-exibido';
import { inicioDoSilencio } from '../instrumentos/perda-de-sinal';
import type { Instrument, Reading, TarpLevel } from '../types';
import { avaliarBannerAnomalia, type TarpParaBanner } from './anomalias';

export interface InstrumentWithLevel {
  /**
   * GUPM-303 — discriminante da união `ItemAtencao`. Obrigatório de propósito: o bloco
   * "Precisa da sua atenção" passou a ter dois tipos de card e o `switch` no componente
   * precisa ser exaustivo. Sem discriminante, um card novo entraria renderizado como se
   * fosse nível medido.
   */
  kind: 'nivel';
  instrument: Instrument;
  /**
   * C-24 — código da barragem, `null` quando ela não está na lista (query de barragens
   * falhou, barragem inativa). 18 códigos se repetem entre A, B e C em produção, e o sino
   * mostra o item fora do Painel: "INA-02 em Alerta" sozinho não diz a quem despachar.
   */
  damCode: string | null;
  level: TarpLevel;
  /** Valor da leitura mais recente (para o texto técnico do RT); null sem leitura. */
  value: number | null;
  /** Unidade exibida do tipo do instrumento (ex.: 'kPa', 'm', 'mm'). */
  unit: string;
  /** Instante da leitura que gerou o nível — mede a carência pós-baixa (GUPM-221). */
  ts: Date;
}

/** Retorna instrumentos cuja leitura mais recente é não-normal, com nível/valor/unidade. */
export function computeBadInstruments(
  instruments: Instrument[],
  readings: Reading[],
  // Obrigatório de propósito (lição do GUPM-325): com default `[]` um chamador que
  // esquecesse ficaria com a barragem sempre "não identificada", sem nada quebrar.
  dams: { id: string; code: string }[],
): InstrumentWithLevel[] {
  const codePorDam = new Map(dams.map((d) => [d.id, d.code]));
  const result: InstrumentWithLevel[] = [];
  // GUPM-270 — retirado não pede ação: fora de "Precisa da sua atenção" e do
  // auto-open da TARP. Instrumento que saiu do monitoramento não gera plano de ação.
  // GUPM-307 — declarado fora de serviço, idem, e é o objeto deste card: o INA-12A
  // oferecia TARP de emergência a partir de uma leitura de 20/08, seis dias depois de
  // sair de campo. Já está sendo tratado; oferecer plano de ação convida a abrir TARP
  // para trabalho em curso.
  for (const inst of instruments.filter((i) => i.isActive && !foraDeServico(i))) {
    // C-88 — instrumento exibido como sem sinal não tem leitura atual: o card dele é o de
    // sem sinal (`computeSemSinal`). Um tiltímetro mudo cuja última leitura era ALERTA
    // entrava nas duas listas — dois cards com a mesma `key`. (PZ/INA mudo segue aqui, com
    // o nível da última leitura: o portão do GUPM-303 só mostra sem sinal para TILT.)
    if (semSinalVisivel(inst)) continue;
    // GUPM-270 — multi-canal: a leitura que governa é a do PIOR canal com faixa.
    // Com `sort` por `ts` a escolha entre canais do mesmo instante era arbitrária, e
    // um tiltímetro em ALERTA podia ficar FORA desta lista — e portanto fora do
    // "Precisa da sua atenção" e do auto-open da TARP.
    const latest = leituraGovernante(
      readings.filter((r) => r.instrumentId === inst.id),
      inst.canaisComFaixa ?? [],
    );
    if (latest?.level && latest.level !== 'NORMAL') {
      result.push({
        kind: 'nivel',
        instrument: inst,
        damCode: codePorDam.get(inst.damId) ?? null,
        level: latest.level,
        value: latest.value,
        unit: unitForType(inst.type),
        ts: latest.ts,
      });
    }
  }
  return result;
}

/** Card de perda de sinal do bloco "Precisa da sua atenção" — GUPM-303. */
export interface ItemSemSinal {
  kind: 'sem-sinal';
  instrument: Instrument;
  /**
   * Código da barragem. Obrigatório: `TIL-01` existe em A, B e C (produção, 24/08/2026),
   * e "TIL-01 sem sinal" sozinho não diz a quem despachar. Vem de `dam.code`, nunca de
   * `dam.name`. `null` quando a barragem não está na lista — o texto diz "Estrutura não
   * identificada" em vez de um "—" que some na frase (C-24, revisão).
   */
  damCode: string | null;
  /** Primeira publicação esperada que não veio — âncora do tempo de silêncio. */
  silencioDesde: Date;
  /** Mesma âncora, no campo que `avaliarBannerAnomalia` usa para a carência. */
  ts: Date;
}

/** Um card do bloco "Precisa da sua atenção": nível medido OU perda de sinal. */
export type ItemAtencao = InstrumentWithLevel | ItemSemSinal;

/**
 * Instrumentos ATIVOS que estão mudos e cujo tipo já exibe perda de sinal — GUPM-303.
 *
 * Separado de `computeBadInstruments` porque a pergunta é outra: lá é "a leitura está
 * fora da faixa?", aqui é "há leitura atual?". Juntar as duas num único laço faria a
 * segunda depender da primeira ter encontrado leitura — e o caso que importa é
 * justamente o da leitura que não chegou.
 *
 * Instrumento sem `lastReadingAt` fica fora: nunca mediu, é `SEM_LEITURA`, e o card de
 * perda de sinal afirmaria uma queda que não houve.
 */
export function computeSemSinal(
  instruments: Instrument[],
  dams: { id: string; code: string }[],
): ItemSemSinal[] {
  const codePorDam = new Map(dams.map((d) => [d.id, d.code]));
  const result: ItemSemSinal[] = [];
  // GUPM-270 — retirado não pede ação: instrumento fora do monitoramento não gera plano.
  for (const inst of instruments.filter((i) => i.isActive)) {
    if (!semSinalVisivel(inst) || !inst.lastReadingAt) continue;
    const silencioDesde = inicioDoSilencio(inst.lastReadingAt, inst.type);
    result.push({
      kind: 'sem-sinal',
      instrument: inst,
      damCode: codePorDam.get(inst.damId) ?? null,
      silencioDesde,
      ts: silencioDesde,
    });
  }
  return result;
}

/**
 * Itens que ainda PEDEM AÇÃO: nível medido + sem sinal, menos os já cobertos por TARP em
 * curso (GUPM-221). Escalada de nível e prazo de baixa vencido nunca são suprimidos.
 *
 * Fonte única do "Precisa da sua atenção" e do sino (C-24).
 */
export function itensQuePedemAcao(
  instruments: Instrument[],
  current: Reading[],
  dams: { id: string; code: string }[],
  tarps: readonly TarpParaBanner[],
  agora: Date,
): ItemAtencao[] {
  // GUPM-303 — o bloco passou a ter dois tipos de card. A perda de sinal entra pela MESMA
  // porta de supressão: TARP em curso silencia, prazo vencido e episódio novo não são
  // silenciados. `level: 'ALERTA'` é o nível do protocolo `TARP-PS-TILT` no catálogo — é
  // com ele que o card se ordena e que a escalada é comparada.
  const itens: ItemAtencao[] = [
    ...computeBadInstruments(instruments, current, dams),
    ...computeSemSinal(instruments, dams),
  ];
  return itens.filter((b) =>
    avaliarBannerAnomalia(
      b.kind === 'sem-sinal'
        ? {
            instrumentId: b.instrument.id,
            level: 'ALERTA',
            ts: b.ts,
            tipo: b.instrument.type,
            origem: 'perda-de-sinal',
            episodioDesde: b.silencioDesde,
          }
        : {
            instrumentId: b.instrument.id,
            level: b.level,
            ts: b.ts,
            // GUPM-306 — `tipo` + `origem` medem se a leitura que venceria a carência
            // ainda é atual. Só o card de LEITURA é limitado por idade.
            tipo: b.instrument.type,
            origem: 'leitura',
          },
      tarps,
      agora,
    ).exibir,
  );
}
